import { Property } from 'shared-types/Property';
import { SpaPackageType, TimeSlotType } from 'shared-types/SpaPackage';
import { fetchSpaPackageConfigGroups } from '@store/entity/spa-package';
import { Moment } from 'moment';
import { useEffect, useState, useRef, Dispatch, SetStateAction } from 'react';

export interface FetchStatusHookType {
  selectedDate: Moment;
  quantity: number;
  selectedTime: string;
  entity: SpaPackageType;
  property: Property;
  fetchSpaPackageConfigGroups: typeof fetchSpaPackageConfigGroups;
}

export const useFetchStatus = ({
  selectedDate,
  quantity,
  selectedTime,
  entity,
  property,
  fetchSpaPackageConfigGroups,
}): { availabilityChecked: boolean } => {
  const { supportsTimeslots, timeslots } = entity;
  const [availabilityChecked, setAvailabilityChecked] = useState(false);
  const timeslotOptions = timeslots?.[selectedDate.format('YYYY-MM-DD')];
  const endTime = timeslotOptions?.find(
    (slot: TimeSlotType) => slot.startTime === selectedTime
  )?.endTime;

  useEffect(() => {
    if (supportsTimeslots) {
      setAvailabilityChecked(false);
    } else {
      fetchSpaPackageConfigGroups({
        propertyId: property.id,
        spaPackageId: entity.id,
        arrivalDate: selectedDate,
        quantity,
      });
      setAvailabilityChecked(true);
    }
  }, [quantity, selectedDate.format('YYYY-MM-DD')]);

  useEffect(() => {
    if (supportsTimeslots && timeslotOptions?.length && selectedTime) {
      fetchSpaPackageConfigGroups({
        propertyId: property.id,
        spaPackageId: entity.id,
        arrivalDate: selectedDate,
        quantity,
        startTime: selectedTime,
        endTime,
      });
      setAvailabilityChecked(true);
    }
  }, [timeslotOptions, selectedTime]);

  return { availabilityChecked };
};

export const useQuantity = (
  initValue: number,
  clearFormFields: (_: string[]) => void
): [number, Dispatch<SetStateAction<number>>] => {
  const [count, setCount] = useState(initValue);
  const prevCountRef = useRef<number>();

  useEffect(() => {
    prevCountRef.current = count;
  });

  const prevCount = prevCountRef.current || count;
  if (prevCount > count) {
    const fieldNames = new Array(prevCount - count)
      .fill('')
      .map((_, index) => `guests.${prevCount - index - 1}`);
    clearFormFields(fieldNames);
  }

  return [count, setCount];
};
